import useSWR from 'swr';
import { JsonServiceClient } from '@servicestack/client';
import { each, sortBy } from 'lodash';
import * as dtos from '../library/Uobam/Uobam.dtos';
import { useMemo } from 'react';
import BaseUrl from './baseUrl';

let client = new JsonServiceClient(BaseUrl);

const fetcher = async (methodName, params) => {
	return await client.get(new dtos[methodName](), params).then((response) => {
		return response;
	});
};

const useRoutes = () => {
	const { data, error } = useSWR(['WebSiteRouteMapRequest'], (methodName) => {
		return fetcher(methodName);
	});

	const flattenedRoutes = useMemo(() => {
		const list = [];

		const retrieveRoutes = (route) => {
			const { categoryChild } = route;

			if (categoryChild?.length) {
				each(categoryChild, (child) => {
					child.parent = route;
					list.push(child);
					retrieveRoutes(child);
				});
			}
		};

		if (data && data.result && data.result.length) {
			each(data.result, (route) => {
				list.push(route);
				retrieveRoutes(route);
			});
		}

		return list;
	}, [data]);

	return {
		routes: data ? data.result : [],
		flattenedRoutes,
		isLoading: !error && !data,
		isError: error,
	};
};

const useAds = (r) => {
	const { data, error } = useSWR(['WebSiteADRequest', r], (methodName, r) => {
		return fetcher(methodName, { r });
	});

	const sortedList = useMemo(() => {
		if (data && data.result) return sortBy(data.result, 'sortValue');
		return [];
	}, [data]);

	return {
		list: sortedList,
		isLoading: !error && !data,
		isError: error,
	};
};

const useStockList = () => {
	const { data, error } = useSWR(['WebSiteStockListRequest'], (methodName) => {
		return fetcher(methodName);
	});

	const sortedList = useMemo(() => {
		if (data && data.resultP) return data.resultP;
		return [];
	}, [data]);

	return {
		data,
		list: sortedList,
		isLoading: !error && !data,
		isError: error,
	};
};

const useStockItem = (fundID, r) => {
	const { data, error } = useSWR(['WebSiteStockRequest', fundID, r], (methodName, fundID, r) => {
		return fetcher(methodName, { fundID, r });
	});

	return {
		stockInfo: data || {},
		isLoading: !error && !data,
		isError: error,
	};
};

const useStockPrice = (fundID) => {
	const { data, error } = useSWR(['WebSiteStockPriceRequest', fundID], (methodName, fundID) => {
		return fetcher(methodName, { fundID });
	});

	return {
		stockPrice: data || {},
		isLoading: !error && !data,
		isError: error,
	};
};

const useStockDividend = (ec001, mfundID, fundID, year, start, end) => {
	const { data, error } = useSWR(['WebSiteStockDividendRequest', ec001, mfundID, fundID, year, start, end], (methodName, ec001, mfundID, fundID, year, start, end) => {
		return fetcher(methodName, { ec001, mfundID, fundID, year, start, end });
	});

	return {
		stockDividend: data || {},
		isLoading: !error && !data,
		isError: error,
	};
};

const useArticles = (u, r, p, n, a) => {
	const { data, error } = useSWR(['WebSiteFrontRequest', u, r, p, n, a], (methodName, u, r, p, n, a) => {
		return fetcher(methodName, { u, r, p, n, a });
	});

	return {
		articles: data?.result || [],
		breadcrumbs: data?.breadcrumb || [],
		totalPage: data?.totalPage || 0,
		isLoading: !error && !data,
		isError: error,
	};
};

const useFundWarning = () => {
	const { data, error } = useSWR(['WebSiteFrontRequest', '/fundwarning'], (methodName, r) => {
		return fetcher(methodName, { r });
	});

	return {
		fundWarning: data?.result?.[0]?.contents || '',
		isLoading: !error && !data,
		isError: error,
	};
};

const useETFPCF = (fundID, pcfDate) => {
	const { data, error } = useSWR(['WebSitePcfRequest', fundID, pcfDate], (methodName, fundID, pcfDate) => {
		if (!fundID) return { data: null, error: true }
		return fetcher(methodName, { fundID, pcfDate });
	});

	return {
		etfpcf: data || null,
		isLoading: !error && !data,
		isError: error,
	};
};

const useETFIndexTrack = (ec006) => {
	const { data, error } = useSWR(['WebSiteIndexChangeRequest', ec006], (methodName, ec006) => {
		return fetcher(methodName, { ec006 });
	});
	return {
		apiData: data?.result || null,
		isLoading: !error && !data,
		isError: error,
	};
};

const useETFEstimate = () => {
	const { data, error } = useSWR(['WebSiteEstimateRequest'], (methodName) => {
		return fetcher(methodName);
	}, { refreshInterval: 15000 });

	return {
		apiData: data?.result || null,
		note: data?.note || null,
		isLoading: !error && !data,
		isError: error,
	};
};

const useFundSizeList = () => {
	const { data, error } = useSWR(['WebSitefundSizeListRequest'], (methodName) => {
		return fetcher(methodName);
	});

	return {
		apiData: data,
		isLoading: !error && !data,
		isError: error,
	};
};

export { useRoutes, useAds, useStockList, useStockItem, useStockPrice, useStockDividend, useArticles, useETFPCF, useETFIndexTrack, useETFEstimate, useFundSizeList, fetcher, useFundWarning };
